import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { subscribeInvalidation } from '../../services/dashboard-invalidation'
import { useInfiniteQuery } from '@tanstack/react-query'
import { canRetainReadData } from '../../services/api/read-revalidation'
import type { InteractionCallsRepository } from './interaction-calls-repository'

export function useInteractionCalls(
  repository: InteractionCallsRepository,
  tenantId: string,
  aggregateId: string | null,
  active: boolean,
  activityAtMs: number | null = null
) {
  const [hintsHealthy, setHintsHealthy] = useState(false)
  // Each page records the conversation activity known when its request
  // started, so only a request issued after an activity can vouch for it.
  const knownActivityAtMs = useRef(activityAtMs)
  useLayoutEffect(() => {
    knownActivityAtMs.current = activityAtMs
  }, [activityAtMs])
  const query = useInfiniteQuery({
    queryKey: ['interaction-calls', tenantId, aggregateId],
    queryFn: async ({ pageParam, signal }) => {
      const issuedForActivityAtMs = knownActivityAtMs.current
      const page = await repository.getCalls(
        tenantId,
        aggregateId!,
        pageParam,
        signal
      )
      return { ...page, issuedForActivityAtMs }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: active && aggregateId !== null,
    staleTime: 30_000,
    refetchInterval: active ? (hintsHealthy ? 120_000 : 30_000) : false,
  })
  const { refetch } = query
  useEffect(() => {
    setHintsHealthy(false)
    if (!active || !aggregateId) return
    return subscribeInvalidation(
      tenantId,
      (healthy) => {
        setHintsHealthy(healthy)
        void refetch({ cancelRefetch: false })
      },
      ['conversations', 'aggregates']
    )
  }, [tenantId, aggregateId, active, refetch])
  const issuedForActivityAtMs = query.data?.pages[0]?.issuedForActivityAtMs
  useEffect(() => {
    if (
      active &&
      aggregateId &&
      activityAtMs !== null &&
      issuedForActivityAtMs !== undefined &&
      (issuedForActivityAtMs === null || issuedForActivityAtMs < activityAtMs)
    ) {
      void refetch({ cancelRefetch: false })
    }
  }, [
    active,
    aggregateId,
    activityAtMs,
    issuedForActivityAtMs,
    query.dataUpdatedAt,
    refetch,
  ])
  const usable =
    query.data && (!query.isError || canRetainReadData(query.error))
  const seen = new Set<string>()
  const calls = usable
    ? query.data.pages
        .flatMap((page) => page.calls)
        .filter((call) => {
          if (seen.has(call.callRef)) return false
          seen.add(call.callRef)
          return true
        })
    : []
  return {
    status:
      !active || !aggregateId
        ? ('idle' as const)
        : usable
          ? ('ready' as const)
          : query.isError
            ? ('error' as const)
            : ('loading' as const),
    calls,
    // Null whenever the displayed calls are not a successful, current read.
    currentThroughActivityAtMs:
      active && aggregateId && query.data && !query.isError
        ? (issuedForActivityAtMs ?? null)
        : null,
    cursor: usable ? (query.data.pages.at(-1)?.nextCursor ?? null) : null,
    hasOlder: Boolean(usable && query.hasNextPage),
    loadingOlder: query.isFetchingNextPage,
    refreshFailed: Boolean(usable && query.isError),
    retry: () => {
      void query.refetch()
    },
    loadOlder: () => {
      if (active && aggregateId && query.hasNextPage && !query.isFetching)
        void query.fetchNextPage()
    },
  }
}
